import React, { useEffect, useRef, useState } from "react";
import api from "@/api";
import { Plus, Trash2, Key, Copy, Check, AlertTriangle, Download, Upload } from "lucide-react";
import { copyToClipboard } from "@/utils/clipboard";

// ── Import Modal ───────────────────────────────────────────────────────────────

function ImportModal({ schemas, onClose, onSave }) {
  const [json, setJson] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = ev => setJson(ev.target.result);
    reader.readAsText(f);
  };

  const handleImport = async () => {
    setError("");
    let parsed;
    try { parsed = JSON.parse(json); } catch { setError("Invalid JSON"); return; }
    setSaving(true);
    try {
      const r = await api.post("/api-keys/import", parsed);
      onSave(r.data.raw_key);
    } catch (err) {
      setError(err.response?.data?.detail || "Import failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <Upload size={14} className="text-[#006080]" />
          <h3 className="font-bold text-slate-900 text-sm">Import API Key Config</h3>
        </div>
        <div className="p-5 space-y-3">
          {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
          <div className="flex items-center gap-2">
            <button onClick={() => fileRef.current?.click()} className="h-8 px-3 text-xs font-medium border border-slate-200 rounded-md hover:bg-slate-50 text-slate-600">Choose JSON file</button>
            <span className="text-xs text-slate-400">or paste JSON below</span>
            <input ref={fileRef} type="file" accept=".json" onChange={handleFile} className="hidden" />
          </div>
          <textarea value={json} onChange={e => setJson(e.target.value)} rows={5}
            placeholder='{"name": "My Key", "schema_id": "..."}'
            className="w-full px-3 py-2 text-xs font-mono border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] resize-none" />
        </div>
        <div className="px-5 pb-4 flex gap-2">
          <button onClick={onClose} className="flex-1 h-8 border border-slate-200 text-sm text-slate-600 rounded-md hover:bg-slate-50">Cancel</button>
          <button onClick={handleImport} disabled={saving || !json.trim()} className="flex-1 h-8 bg-[#006080] text-white text-sm font-medium rounded-md disabled:opacity-50">
            {saving ? "Importing…" : "Import Key"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── New Key Modal ──────────────────────────────────────────────────────────────

function NewKeyModal({ schemas, onClose, onSave }) {
  const [form, setForm] = useState({ name: "", schema_id: "" });
  const [saving, setSaving] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/api-keys", form);
      setCreatedKey(r.data.raw_key);
    } catch (err) {
      console.error(err);
    } finally { setSaving(false); }
  };

  const copyKey = () => {
    copyToClipboard(createdKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (createdKey) return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 w-full max-w-md animate-fade-in" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-900">API Key Created</h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md flex items-center gap-2 text-sm text-amber-700">
            <AlertTriangle size={14} className="flex-shrink-0" />
            Copy this key now. It won't be shown again.
          </div>
          <div className="flex items-center gap-2">
            <code data-testid="new-api-key-value" className="flex-1 text-xs font-mono bg-slate-50 border border-slate-200 rounded px-3 py-2 overflow-x-auto">{createdKey}</code>
            <button onClick={copyKey} data-testid="copy-api-key-button" className="p-2 hover:bg-slate-100 rounded">
              {copied ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} className="text-slate-400" />}
            </button>
          </div>
          <button onClick={() => { onSave(); }} data-testid="close-key-modal" className="w-full h-9 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66]">Done</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 w-full max-w-md animate-fade-in" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-900">Create API Key</h3>
        </div>
        <form onSubmit={handleSave} className="p-6 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Key Name</label>
            <input data-testid="apikey-name-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="e.g., Production, My Integration" className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Default Schema (optional)</label>
            <select data-testid="apikey-schema-select" value={form.schema_id} onChange={e => setForm(f => ({ ...f, schema_id: e.target.value }))} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white">
              <option value="">No default schema</option>
              {schemas.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-9 border border-slate-200 text-sm font-medium text-slate-700 rounded-md hover:bg-slate-50">Cancel</button>
            <button data-testid="create-apikey-button" type="submit" disabled={saving} className="flex-1 h-9 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66] disabled:opacity-50">
              {saving ? "Creating..." : "Create Key"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Duplicate Key Display ──────────────────────────────────────────────────────

function DuplicateKeyModal({ rawKey, onClose }) {
  const [copied, setCopied] = useState(false);
  const copyKey = () => { copyToClipboard(rawKey); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-900">Key Duplicated</h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md flex items-center gap-2 text-sm text-amber-700">
            <AlertTriangle size={14} className="flex-shrink-0" /> Copy this key now. It won't be shown again.
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs font-mono bg-slate-50 border border-slate-200 rounded px-3 py-2 overflow-x-auto">{rawKey}</code>
            <button onClick={copyKey} className="p-2 hover:bg-slate-100 rounded">
              {copied ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} className="text-slate-400" />}
            </button>
          </div>
          <button onClick={onClose} className="w-full h-9 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66]">Done</button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [newKeyRaw, setNewKeyRaw] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchAll = () => {
    setLoading(true);
    Promise.all([api.get("/api-keys"), api.get("/schemas")])
      .then(([k, s]) => { setKeys(k.data); setSchemas(s.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Revoke this API key?")) return;
    await api.delete(`/api-keys/${id}`);
    fetchAll();
  };

  const handleDuplicate = async (k) => {
    try {
      const r = await api.post(`/api-keys/${k.id}/duplicate`);
      setNewKeyRaw(r.data.raw_key);
      fetchAll();
    } catch {}
  };

  const handleExport = async (k) => {
    try {
      const r = await api.get(`/api-keys/${k.id}/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${k.name.replace(/\s+/g, "_")}.apikey.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">API Keys</h2>
          <p className="text-sm text-slate-500">Manage keys for programmatic document ingestion.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowImport(true)} data-testid="import-apikey-button"
            className="flex items-center gap-1.5 h-9 px-3 text-sm font-medium border border-slate-200 bg-white text-slate-600 rounded-md hover:bg-slate-50 transition-colors">
            <Upload size={13} /> Import
          </button>
          <button onClick={() => setShowModal(true)} data-testid="create-apikey-modal-button"
            className="flex items-center gap-2 h-9 px-4 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors">
            <Plus size={14} /> New Key
          </button>
        </div>
      </div>

      {/* Ingest endpoint docs */}
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">API Ingest Endpoint</h3>
        <div className="space-y-2">
          <code className="block text-xs font-mono bg-slate-50 border border-slate-200 rounded px-3 py-2 text-slate-800">
            POST {BACKEND_URL}/api/v1/ingest
          </code>
          <p className="text-xs text-slate-500">Send documents as base64 or hosted URL. Include your key in the <code className="regex-code">X-Api-Key</code> header.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" /></div>
      ) : keys.length === 0 ? (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
          <Key size={28} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500 mb-3">No API keys yet</p>
          <button onClick={() => setShowModal(true)} className="h-9 px-4 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66]">Create first key</button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {["Name", "Key", "Default Schema", "Last Used", "Status", ""].map(h => (
                  <th key={h} className="text-left text-xs font-bold uppercase tracking-wide text-slate-500 px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {keys.map((k, i) => {
                const schema = schemas.find(s => s.id === k.schema_id);
                return (
                  <tr key={k.id} data-testid={`apikey-row-${k.id}`} className={`border-b border-slate-100 hover:bg-slate-50 ${i % 2 === 1 ? "bg-slate-50/50" : ""}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{k.name}</td>
                    <td className="px-4 py-3"><code className="regex-code">{k.key_prefix}...</code></td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{schema?.name || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{k.last_used_at ? k.last_used_at.slice(0, 10) : "Never"}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${k.is_active ? "bg-[#E5F0F2] text-[#006080]" : "bg-slate-100 text-slate-400"}`}>{k.is_active ? "Active" : "Revoked"}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleDuplicate(k)} data-testid={`duplicate-apikey-${k.id}`} title="Duplicate"
                          className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600"><Copy size={12} /></button>
                        <button onClick={() => handleExport(k)} data-testid={`export-apikey-${k.id}`} title="Export config"
                          className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600"><Download size={12} /></button>
                        <button onClick={() => handleDelete(k.id)} data-testid={`delete-apikey-${k.id}`}
                          className="p-1.5 hover:bg-red-50 rounded text-slate-400 hover:text-red-500"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && <NewKeyModal schemas={schemas} onClose={() => setShowModal(false)} onSave={() => { setShowModal(false); fetchAll(); }} />}
      {showImport && <ImportModal schemas={schemas} onClose={() => setShowImport(false)} onSave={(raw) => { setShowImport(false); setNewKeyRaw(raw); fetchAll(); }} />}
      {newKeyRaw && <DuplicateKeyModal rawKey={newKeyRaw} onClose={() => setNewKeyRaw(null)} />}
    </div>
  );
}
